import { gzipSync, gunzipSync, strToU8, strFromU8 } from 'fflate'
import { bytesToBase64Url, base64UrlToBytes } from './base64url'

/** Format marker for gzip+base64url material data (MineralMinds gzip v1). */
export const MATERIAL_DATA_MARKER = 'MMZ1:'

/**
 * Size caps for decompressing untrusted (URL `ml` / STOMP) gzip payloads — guards against
 * zip bombs. Legitimate miniLot material data is ~58KB; these are generous but bound memory.
 */
const MAX_COMPRESSED_BYTES = 256 * 1024
const MAX_DECOMPRESSED_BYTES = 2 * 1024 * 1024

/**
 * gunzip with input/output size caps; returns null if a cap is exceeded or on error.
 * fflate allocates the output buffer from the gzip ISIZE trailer up front, so the declared
 * uncompressed size is rejected BEFORE calling gunzipSync.
 */
function boundedGunzip(bytes: Uint8Array): Uint8Array | null {
	try {
		if (bytes.length > MAX_COMPRESSED_BYTES) return null
		// gzip trailer: last 4 bytes = uncompressed size mod 2^32, little-endian.
		if (bytes.length >= 4) {
			const declaredSize = new DataView(bytes.buffer, bytes.byteOffset + bytes.length - 4, 4).getUint32(0, true)
			if (declaredSize > MAX_DECOMPRESSED_BYTES) return null
		}
		const decompressed = gunzipSync(bytes)
		if (decompressed.length > MAX_DECOMPRESSED_BYTES) return null
		return decompressed
	} catch {
		return null
	}
}

/** gzip + base64url-encode any JSON value. URL/query-param safe. */
export function compressToBase64Url(value: unknown): string {
	return bytesToBase64Url(gzipSync(strToU8(JSON.stringify(value))))
}

/** Inverse of {@link compressToBase64Url}; returns null on malformed or oversized input. */
export function decompressFromBase64Url<T>(encoded: string): T | null {
	try {
		const decompressed = boundedGunzip(base64UrlToBytes(encoded))
		return decompressed ? (JSON.parse(strFromU8(decompressed)) as T) : null
	} catch {
		return null
	}
}

/** Marker + gzip + base64url of a string (for the STOMP materialData field). */
export function compressString(text: string): string {
	return MATERIAL_DATA_MARKER + bytesToBase64Url(gzipSync(strToU8(text)))
}

/**
 * Inverse of {@link compressString}. An unmarked string is returned unchanged
 * (backward compat). Returns null when a marked string is malformed or oversized.
 */
export function decompressString(text: string): string | null {
	if (!text.startsWith(MATERIAL_DATA_MARKER)) return text
	try {
		const decompressed = boundedGunzip(base64UrlToBytes(text.slice(MATERIAL_DATA_MARKER.length)))
		return decompressed ? strFromU8(decompressed) : null
	} catch {
		return null
	}
}
